/**
 * Handles drag interactions for CSS carousel
 * Distinguishes between drag/swipe and click, provides smooth scrolling
 */
class ResourceListCarousel {
  constructor(element) {
    this.element = element;
    this.track = element.querySelector('.resource-list__carousel-track');
    if (!this.track) return;

    this.isDragging = false;
    this.hasDragged = false;
    this.startX = 0;
    this.startY = 0;
    this.scrollLeft = 0;
    this.lastX = 0; // Track last mouse position for smooth incremental updates
    this.dragThreshold = 10; // Minimum pixels to move before considering it a drag (increased to allow clicks)
    this.clickedElement = null;
    this.scrollTimeout = null;
    this.animationFrame = null;
    this.lastScrollLeft = 0;
    this.animationObserver = null; // For watching animation loop

    this.init();
  }

  init() {
    // Batch all layout reads first to avoid forced reflows
    const trackScrollWidth = this.track.scrollWidth;
    const elementClientWidth = this.element.clientWidth;

    // Ensure element is scrollable
    if (trackScrollWidth <= elementClientWidth) {
      // Content fits, no need for carousel
      return;
    }

    // On mobile/touch devices, use native scrolling instead of JavaScript
    // This provides better performance and smoother interaction
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) {
      // Just enable native scrolling - don't add drag handlers
      this.element.style.cursor = 'auto';
      return;
    }
    
    // Set initial scroll position to respect padding
    // Get the padding value from computed styles (batch this read)
    const computedStyle = getComputedStyle(this.track);
    const paddingStart = parseFloat(computedStyle.paddingInlineStart) || 0;
    const currentScrollLeft = this.element.scrollLeft;
    
    // Batch style write
    if (paddingStart > 0 && currentScrollLeft === 0) {
      this.element.scrollLeft = paddingStart;
    }
    
    // Prevent image dragging - allow carousel scrolling instead
    // Set draggable to false on all images to prevent browser's native drag behavior
    // CSS already handles -webkit-user-drag: none, so we just need to set the attribute
    const images = this.element.querySelectorAll('img');
    images.forEach(img => {
      img.setAttribute('draggable', 'false');
      // Also prevent dragstart on images to stop browser drag
      img.addEventListener('dragstart', (e) => {
        e.preventDefault();
        return false;
      }, { passive: false });
    });
    
    // Prevent dragstart on the entire carousel to catch any missed images
    this.element.addEventListener('dragstart', (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    }, { passive: false });
    
    // Mouse events for dragging - attach to carousel only (track events bubble up)
    this.element.addEventListener('mousedown', this.handleMouseDown.bind(this), { passive: false });
    document.addEventListener('mousemove', this.handleMouseMove.bind(this), { passive: false });
    document.addEventListener('mouseup', this.handleMouseUp.bind(this), { passive: true });

    // Touch events for mobile - use passive for better performance
    // Only preventDefault when we're actually dragging horizontally
    this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.element.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });

    // Handle native scroll for smooth momentum
    this.element.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    
    // Prevent text selection during drag
    this.element.addEventListener('selectstart', (e) => {
      if (this.isDragging) {
        e.preventDefault();
      }
    });
    
    // Setup auto-scroll animation (non-looping)
    this.setupAnimationLoop();
  }
  
  setupAnimationLoop() {
    // Simple auto-scroll animation (non-looping for simplicity)
    let animationSpeed = 0.4; // pixels per frame
    let animationId = null;
    let isPaused = false;
    
    const animate = () => {
      // Check if element is still in DOM
      if (!this.element.parentNode) {
        return;
      }
      
      // Batch layout reads to avoid forced reflows
      const scrollWidth = this.element.scrollWidth;
      const clientWidth = this.element.clientWidth;
      const scrollLeft = this.element.scrollLeft;
      const maxScroll = scrollWidth - clientWidth;
      
      // Don't animate if at end, during interaction, or paused
      if (scrollLeft >= maxScroll - 1 ||
          this.isDragging || 
          this.element.classList.contains('is-interacting') ||
          this.element.classList.contains('is-scrolling') ||
          isPaused) {
        animationId = requestAnimationFrame(animate);
        return;
      }
      
      // Auto-scroll forward (this is a write, but it's necessary for animation)
      this.element.scrollLeft += animationSpeed;
      
      animationId = requestAnimationFrame(animate);
    };
    
    // Pause on hover (but allow drag to work)
    this.element.addEventListener('mouseenter', () => {
      if (!this.isDragging) {
        isPaused = true;
      }
    });
    
    this.element.addEventListener('mouseleave', () => {
      isPaused = false;
    });
    
    // Start animation after a short delay
    setTimeout(() => {
      animationId = requestAnimationFrame(animate);
    }, 500);
    
    this.animationId = animationId;
  }

  handleMouseDown(e) {
    // Only handle if the event is within our carousel
    if (!this.element.contains(e.target)) return;
    
    // Store the clicked element to check if it's a link
    this.clickedElement = e.target.closest('a, button, [role="button"]');
    
    // Reset drag state
    this.isDragging = false;
    this.hasDragged = false;
    this.startX = e.pageX;
    this.startY = e.pageY;
    this.lastX = e.pageX; // Initialize lastX to current position for smooth start
    this.scrollLeft = this.element.scrollLeft;
    
    // Pause animation immediately on interaction
    this.element.classList.add('is-interacting');
    
    // Don't prevent default - allow clicks to work normally
    // We'll only prevent default when we actually start dragging (in handleMouseMove)
  }

  handleMouseMove(e) {
    if (this.startX === 0) return; // Not in a drag sequence
    
    const deltaX = Math.abs(e.pageX - this.startX);
    const deltaY = Math.abs(e.pageY - this.startY);
    
    // Only consider it dragging if horizontal movement is greater than vertical and exceeds threshold
    if (!this.hasDragged && (deltaX > this.dragThreshold || deltaY > this.dragThreshold)) {
      if (deltaX > deltaY && deltaX > this.dragThreshold) {
        this.isDragging = true;
        this.hasDragged = true;
        this.element.classList.add('is-dragging');
        this.element.style.cursor = 'grabbing';
        
        // Store the current scroll position when drag starts
        this.scrollLeft = this.element.scrollLeft;
        this.lastScrollLeft = this.scrollLeft;
        
        // Prevent click if we're dragging - but only disable pointer events temporarily
        if (this.clickedElement) {
          this.clickedElement.style.pointerEvents = 'none';
        }
        
        // Prevent default once we start dragging to stop image drag
        e.preventDefault();
        e.stopPropagation();
      }
    }
    
    if (this.isDragging) {
      e.preventDefault();
      e.stopPropagation();
      
      // Use incremental updates for pixel-perfect smooth dragging
      const currentX = e.pageX;
      const deltaX = currentX - this.lastX;
      this.lastX = currentX;
      
      // Update scroll position incrementally for smooth pixel-by-pixel movement
      // Negative deltaX means dragging right (scroll left), positive means dragging left (scroll right)
      this.element.scrollLeft -= deltaX;
    }
  }

  handleMouseUp(e) {
    const wasDragging = this.isDragging;
    
    // Cancel any pending animation frame
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    
    // Only prevent default if we were actually dragging
    if (this.isDragging) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Only prevent click if we actually dragged (moved more than threshold)
    if (wasDragging && this.hasDragged && this.clickedElement) {
      const preventClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        this.clickedElement.removeEventListener('click', preventClick, true);
      };
      this.clickedElement.addEventListener('click', preventClick, true);
      setTimeout(() => {
        this.clickedElement.removeEventListener('click', preventClick, true);
      }, 300);
    }
    
    // Always restore pointer events immediately if we didn't drag
    if (this.clickedElement) {
      if (!wasDragging || !this.hasDragged) {
        // Didn't drag - restore immediately and allow click
        this.clickedElement.style.pointerEvents = '';
        this.clickedElement = null;
      } else {
        // Did drag - restore after a short delay
        setTimeout(() => {
          if (this.clickedElement) {
            this.clickedElement.style.pointerEvents = '';
            this.clickedElement = null;
          }
        }, 50);
      }
    }
    
    this.isDragging = false;
    this.hasDragged = false;
    this.startX = 0;
    this.startY = 0;
    this.lastX = 0; // Reset last position
    this.element.classList.remove('is-dragging');
    this.element.style.cursor = 'grab';
    
    // Remove interaction class after a delay
    setTimeout(() => {
      this.element.classList.remove('is-interacting');
    }, 100);
  }

  handleTouchStart(e) {
    const touch = e.touches[0];
    this.clickedElement = e.target.closest('a, button, [role="button"]');
    
    this.isDragging = false;
    this.hasDragged = false;
    this.startX = touch.pageX;
    this.startY = touch.pageY;
    this.lastX = touch.pageX; // Initialize lastX to current position for smooth start
    this.scrollLeft = this.element.scrollLeft;
    
    this.element.classList.add('is-interacting');
  }

  handleTouchMove(e) {
    if (this.startX === 0) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.pageX - this.startX);
    const deltaY = Math.abs(touch.pageY - this.startY);
    
    // Only take control if it's clearly a horizontal swipe
    // Allow native scrolling for vertical movement
    if (!this.hasDragged && (deltaX > this.dragThreshold || deltaY > this.dragThreshold)) {
      // Only prevent default and take control if horizontal movement is significantly greater
      if (deltaX > deltaY && deltaX > this.dragThreshold * 2) {
        this.isDragging = true;
        this.hasDragged = true;
        this.element.classList.add('is-dragging');
        
        // Store the current scroll position when drag starts
        this.scrollLeft = this.element.scrollLeft;
        this.lastScrollLeft = this.scrollLeft;
        this.lastX = touch.pageX; // Initialize last position
        
        // Prevent click if we're dragging
        if (this.clickedElement) {
          this.clickedElement.style.pointerEvents = 'none';
        }
        
        e.preventDefault();
      } else {
        // Vertical or ambiguous movement - let native scroll handle it
        return;
      }
    }
    
    if (this.isDragging) {
      e.preventDefault();
      
      // Use incremental updates for pixel-perfect smooth dragging
      const currentX = touch.pageX;
      const deltaX = currentX - this.lastX;
      this.lastX = currentX;
      
      // Update scroll position incrementally for smooth pixel-by-pixel movement
      // Negative deltaX means dragging right (scroll left), positive means dragging left (scroll right)
      this.element.scrollLeft -= deltaX;
    }
  }

  handleTouchEnd(e) {
    const wasDragging = this.isDragging;
    
    // Cancel any pending animation frame
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    
    if (this.isDragging) {
      e.preventDefault();
      
      // Animation will resume automatically via JavaScript
    }
    
    // If we dragged, prevent click events on links
    if (wasDragging && this.clickedElement) {
      const preventClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        this.clickedElement.removeEventListener('click', preventClick, true);
      };
      this.clickedElement.addEventListener('click', preventClick, true);
      setTimeout(() => {
        this.clickedElement.removeEventListener('click', preventClick, true);
      }, 300);
    }
    
    // Restore pointer events
    if (this.clickedElement) {
      this.clickedElement.style.pointerEvents = '';
      this.clickedElement = null;
    }
    
    this.isDragging = false;
    this.hasDragged = false;
    this.startX = 0;
    this.startY = 0;
    this.lastX = 0; // Reset last position
    this.element.classList.remove('is-dragging');
    
    setTimeout(() => {
      this.element.classList.remove('is-interacting');
    }, 100);
  }

  handleScroll() {
    // Pause animation during scroll
    this.element.classList.add('is-scrolling');
    
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.element.classList.remove('is-scrolling');
    }, 150);
  }
}

// Fix carousel layout: ensure title and author are below image, not inside it
function fixCarouselLayout(carousel) {
  const cards = carousel.querySelectorAll('.product-card, .resource-card');
  cards.forEach(card => {
    // Find the card gallery
    const cardGallery = card.querySelector('.card-gallery');
    if (!cardGallery) return;
    
    // Find title wrapper and author
    const titleWrapper = card.querySelector('.product-title-wrapper');
    const titleContainer = card.querySelector('.product-title-container');
    const author = card.querySelector('.product-author');
    const zoomOutDetails = card.querySelector('.product-grid-view-zoom-out--details');
    const cardContent = card.querySelector('.product-card__content, .resource-card__content');
    
    // Hide zoom-out details in carousels - completely remove from layout
    if (zoomOutDetails) {
      zoomOutDetails.style.display = 'none';
      zoomOutDetails.style.visibility = 'hidden';
      zoomOutDetails.style.position = 'absolute';
      zoomOutDetails.style.opacity = '0';
      zoomOutDetails.style.height = '0';
      zoomOutDetails.style.width = '0';
      zoomOutDetails.style.overflow = 'hidden';
      zoomOutDetails.style.pointerEvents = 'none';
      zoomOutDetails.setAttribute('aria-hidden', 'true');
    }
    
    // Ensure title and author are in card content, not in gallery
    if (cardContent && cardGallery) {
      // If title/author are somehow inside the gallery, move them out
      if (cardGallery.contains(titleWrapper)) {
        cardContent.appendChild(titleWrapper);
      }
      if (cardGallery.contains(titleContainer)) {
        if (titleWrapper && titleWrapper.parentNode === cardContent) {
          titleWrapper.appendChild(titleContainer);
        } else {
          cardContent.appendChild(titleContainer);
        }
      }
      if (cardGallery.contains(author)) {
        if (titleWrapper && titleWrapper.parentNode === cardContent) {
          titleWrapper.appendChild(author);
        } else {
          cardContent.appendChild(author);
        }
      }
      
    // Ensure proper DOM order: gallery first, then title/author, then price
    // Quick-add should stay inside gallery but be absolutely positioned
    const quickAdd = card.querySelector('.quick-add, quick-add-component');
    if (quickAdd) {
      // Ensure quick-add is inside gallery and absolutely positioned
      if (!cardGallery.contains(quickAdd) && quickAdd.parentNode === cardContent) {
        cardGallery.appendChild(quickAdd);
      }
      quickAdd.style.position = 'absolute';
      quickAdd.style.top = '8px';
      quickAdd.style.right = '8px';
      quickAdd.style.width = 'auto';
      quickAdd.style.height = 'auto';
      quickAdd.style.margin = '0';
      quickAdd.style.padding = '0';
      quickAdd.style.zIndex = '10';
    }
      
      // Move gallery to be first child if it's not
      if (cardGallery.parentNode === cardContent && cardContent.firstChild !== cardGallery) {
        cardContent.insertBefore(cardGallery, cardContent.firstChild);
      }
      
      // Ensure title wrapper comes after gallery (not after quick-add)
      if (titleWrapper && titleWrapper.parentNode === cardContent) {
        // If title is before gallery, move it after
        if (titleWrapper.compareDocumentPosition(cardGallery) & Node.DOCUMENT_POSITION_PRECEDING) {
          cardContent.insertBefore(titleWrapper, cardGallery.nextSibling);
        }
        // If title is somehow after quick-add but inside gallery, move it out
        if (cardGallery.contains(titleWrapper)) {
          cardContent.insertBefore(titleWrapper, cardGallery.nextSibling);
        }
      }
    }
    
    // Force reset positioning styles
    if (titleWrapper) {
      titleWrapper.style.position = 'static';
      titleWrapper.style.top = 'auto';
      titleWrapper.style.bottom = 'auto';
      titleWrapper.style.left = 'auto';
      titleWrapper.style.right = 'auto';
    }
    if (author) {
      author.style.position = 'static';
      author.style.top = 'auto';
      author.style.bottom = 'auto';
      author.style.left = 'auto';
      author.style.right = 'auto';
    }
    
    // Ensure price is not overlapping image
    const priceElement = card.querySelector('product-price');
    if (priceElement) {
      priceElement.style.position = 'static';
      priceElement.style.marginTop = '0';
      priceElement.style.marginBottom = '0';
      priceElement.style.top = 'auto';
      priceElement.style.bottom = 'auto';
      priceElement.style.left = 'auto';
      priceElement.style.right = 'auto';
      priceElement.style.zIndex = 'auto';
      
      // If price is somehow inside gallery, move it out
      if (cardGallery.contains(priceElement)) {
        if (cardContent) {
          cardContent.appendChild(priceElement);
        }
      }
    }
    
  });
}

// Initialize carousels lazily using IntersectionObserver for better performance
function initializeCarousels() {
  const carousels = document.querySelectorAll('.resource-list__carousel--css:not([data-initialized])');
  if (carousels.length === 0) return;

  // Use IntersectionObserver to initialize only when carousels are visible or near viewport
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const carousel = entry.target;
        if (!carousel.dataset.initialized) {
          try {
            new ResourceListCarousel(carousel);
            // Fix layout after initialization
            fixCarouselLayout(carousel);
            carousel.dataset.initialized = 'true';
          } catch (error) {
            console.error('Error initializing carousel:', error);
          }
          observer.unobserve(carousel);
        }
      }
    });
  }, {
    rootMargin: '50px' // Start initializing 50px before entering viewport
  });

  carousels.forEach(carousel => {
    observer.observe(carousel);
  });
}

// Initialize immediately if DOM is ready, otherwise wait
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCarousels);
} else {
  // Use requestIdleCallback to defer initialization if possible
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(initializeCarousels, { timeout: 2000 });
  } else {
    initializeCarousels();
  }
}

// No MutationObserver: it caused constant refresh (any DOM change re-ran init; fixCarouselLayout
// mutates DOM). Carousels are initialized once via DOMContentLoaded/requestIdleCallback +
// IntersectionObserver; fixCarouselLayout runs only when a carousel is first initialized.

